import { IconArrowsMove, IconResize, IconRotate, IconSquarePlus, IconTrash } from "@tabler/icons-react";
import { RibbonButton } from "@tredespace/ui/widgets";
import type { JSX, ReactNode } from "react";
import type { PlaneGizmoMode } from "../../engine/planes.ts";
import { addPlane, removePlane, setPlaneMode, setPlaneSnap } from "../../state/planes/planes.actions.ts";
import type { PlanesState } from "../../state/planes/planes.state.ts";

type ModeButton = Readonly<{ mode: PlaneGizmoMode; label: string; icon: ReactNode; tooltip: string }>;

const MODES: readonly ModeButton[] = [
  { mode: "move", label: "Move", icon: <IconArrowsMove />, tooltip: "Drag the arrows — green is height" },
  {
    mode: "resize",
    label: "Resize",
    icon: <IconResize />,
    tooltip: "Drag a corner — the opposite corner stays put",
  },
  {
    mode: "rotate",
    label: "Rotate",
    icon: <IconRotate />,
    tooltip: "Drag the ring to turn about the vertical",
  },
];

/**
 * Plane tool options: add, delete, what the gizmo does, snapping.
 *
 * Returned as a flat list rather than a component because the ribbon section
 * packs columns from its direct children's `size` — wrapped in a component
 * they would all land in one column.
 */
export function planeOptionButtons({ selectedId, mode, snap }: PlanesState): JSX.Element[] {
  return [
    <RibbonButton
      key="add"
      icon={<IconSquarePlus />}
      label="Add plane"
      size="medium"
      onClick={addPlane}
      tooltip="A 6 × 4 m plane at the view's orbit point"
    />,
    <RibbonButton
      key="delete"
      icon={<IconTrash />}
      label="Delete"
      size="medium"
      disabled={selectedId === null}
      onClick={() => selectedId !== null && removePlane(selectedId)}
      tooltip="Remove the selected plane"
    />,
    ...MODES.map((entry) => (
      <RibbonButton
        key={entry.mode}
        icon={entry.icon}
        label={entry.label}
        size="mini"
        selected={mode === entry.mode}
        onClick={() => setPlaneMode(entry.mode)}
        tooltip={entry.tooltip}
      />
    )),
    <RibbonButton
      key="snap"
      label="Snap"
      size="mini"
      selected={snap}
      onClick={() => setPlaneSnap(!snap)}
      tooltip="Snap moves to 0.1 m, rotation to 15°, size to 0.1 m"
    />,
  ];
}
